// Room data-fetching + derived-furniture cluster, extracted verbatim from
// RoomCanvas.tsx (final cleanup pass). Split into TWO hooks so RoomCanvas can
// keep calling useRoomHistory between them exactly where it did before:
//   useRoomQueries          — the four react-query fetches + isCityHall memo
//   useRoomDerivedFurniture — enrichment + cobweb layout + preview/placed memos
// Pure data-in/data-out: no refs, no PIXI, no effects.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMapObjects } from '../../models/mapObject';
import type { HouseFurniture, FurnitureCatalog } from '../../models/apiTypes';
import {
  getCobwebsInteriorObjectsInteriorObjectIdCobwebsGet,
  getFurnitureCatalogFurnitureCatalogGet,
} from '../../client';
import { fetchHouseFurniture } from './state/layoutSync';
import { computeCobwebLayout } from './cobwebLayout';
import type { RenderFurniture } from '../../domain/types';
import type { PreviewItem } from '../../stores/useFurniturePreviewStore';
import { isPracticeRoom } from '../../domain/interiorTutorial';

export function useRoomQueries({ mapObjectId }: { mapObjectId: number }) {
  // The tutorial's practice room lives only in the query cache (tutorial/practiceRoom.ts).
  const practice = isPracticeRoom(mapObjectId);
  // House furniture query (only placed furniture for current room)
  const {
    data: houseFurniture = [],
    isSuccess: houseFurnitureLoaded,
    isFetching: houseFurnitureFetching,
  } = useQuery({
    queryKey: ['houseFurniture', mapObjectId],
    queryFn: () => fetchHouseFurniture(mapObjectId),
    enabled: !!mapObjectId && !practice,
  });

  // Furniture catalog query (fallback to fill missing furniture_catalog)
  const { data: furnitureCatalog = [] } = useQuery({
    queryKey: ['furnitureCatalog'],
    queryFn: async () => {
      const { data } = await getFurnitureCatalogFurnitureCatalogGet({ throwOnError: true });
      return data.items;
    },
    enabled: !!mapObjectId,
  });

  // Get map objects to check if this is a city hall
  const { data: mapObjects = [] } = useQuery({
    queryKey: ['mapObjects'],
    queryFn: () => getMapObjects(),
  });

  // Cobwebs from DB (spawned by cron for inactive houses)
  const { data: cobwebsData = [] } = useQuery({
    queryKey: ['cobwebs', mapObjectId],
    queryFn: async () => {
      const { data } = await getCobwebsInteriorObjectsInteriorObjectIdCobwebsGet({
        path: { interior_object_id: mapObjectId },
        throwOnError: true,
      });
      return data.items;
    },
    enabled: !!mapObjectId && !practice,
    staleTime: 30_000,
  });

  // Check if current room is a city hall
  const isCityHall = useMemo(() => {
    const mapObject = mapObjects.find(obj => obj.id === mapObjectId);
    return mapObject?.type === 'cityhall';
  }, [mapObjects, mapObjectId]);

  return {
    houseFurniture,
    houseFurnitureLoaded,
    houseFurnitureFetching,
    furnitureCatalog,
    mapObjects,
    cobwebsData,
    isCityHall,
  };
}

type RoomQueriesResult = ReturnType<typeof useRoomQueries>;

export function useRoomDerivedFurniture({
  mapObjectId,
  houseFurniture,
  furnitureCatalog,
  mapObjects,
  cobwebsData,
  previewMapObjectId,
  previewItems,
}: {
  mapObjectId: number;
  houseFurniture: RoomQueriesResult['houseFurniture'];
  furnitureCatalog: RoomQueriesResult['furnitureCatalog'];
  mapObjects: RoomQueriesResult['mapObjects'];
  cobwebsData: RoomQueriesResult['cobwebsData'];
  previewMapObjectId: number | null;
  previewItems: PreviewItem[];
}) {
  // Backend now sends furniture_catalog directly; fallback enrichment for edge cases
  const enrichedHouseFurniture = useMemo(() => {
    if (!houseFurniture.length) return houseFurniture;

    return houseFurniture.map(f => {
      if (f.furniture_catalog) return f;

      // Fallback: find catalog item by ID if backend didn't include it
      const catalogItem = furnitureCatalog.find(c => c.id === f.furniture_catalog_id);
      if (catalogItem) {
        return { ...f, furniture_catalog: catalogItem };
      }

      return f;
    });
  }, [houseFurniture, furnitureCatalog]);

  // Cobweb layout (DB rows → deterministic screen positions) extracted to
  // ./room/cobwebLayout.ts. Memoized on the same inputs as before.
  const cobwebs = useMemo(
    () => computeCobwebLayout(
      cobwebsData,
      mapObjects.find(obj => obj.id === mapObjectId),
      enrichedHouseFurniture,
      mapObjectId,
    ),
    [cobwebsData, mapObjects, mapObjectId, enrichedHouseFurniture],
  );

  const previewRenderableFurniture = useMemo<RenderFurniture[]>(() => {
    if (previewMapObjectId !== mapObjectId || previewItems.length === 0) {
      // if (previewItems.length > 0) {
      //   console.warn('[RoomCanvas] Preview items exist but mapObjectId mismatch!', {
      //     previewMapObjectId, mapObjectId, count: previewItems.length,
      //   });
      // }
      return [];
    }
    const result = previewItems.map(p => ({
      id: -100000 - p.instanceId,
      furniture_catalog_id: p.catalogId,
      furniture_catalog: p.furniture,
      is_placed: true,
      room_index: 0,
      x: p.x,
      y: p.y,
      is_mirrored: p.isMirrored,
      is_preview: true,
      placement_mode: p.placement_mode,
    }));
    // console.log('[RoomCanvas] Preview renderable furniture:', {
    //   count: result.length,
    //   ids: result.map(r => r.id),
    //   catalogIds: result.map(r => r.furniture_catalog_id),
    // });
    return result;
  }, [previewItems, previewMapObjectId, mapObjectId]);

  // Filter furniture for current room (roomIndex = 0 for now) + optional preview furniture
  const placedFurniture: RenderFurniture[] = useMemo(() => {
    const realPlaced = enrichedHouseFurniture.filter(
      (f): f is HouseFurniture & { furniture_catalog: FurnitureCatalog } =>
        !!(f.is_placed && f.room_index === 0 && f.furniture_catalog)
    );
    if (previewRenderableFurniture.length === 0) return realPlaced;
    return [...realPlaced, ...previewRenderableFurniture];
  }, [enrichedHouseFurniture, previewRenderableFurniture]);

  return { cobwebs, previewRenderableFurniture, placedFurniture };
}
